import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';

const pickLocalized = (i18nMap, lang) => {
  if (!i18nMap) return '';
  return i18nMap[lang] || i18nMap.en || i18nMap.fr || Object.values(i18nMap)[0] || '';
};

/**
 * Renders sanitized rich-HTML content from a footer link's `content_i18n`.
 * Uses DOMPurify to strip any <script>/onclick/etc. — safe against XSS.
 */
export const RichContentModal = ({ open, onOpenChange, title, contentHtml }) => {
  const { t } = useTranslation();
  const sanitized = useMemo(
    () => (contentHtml ? DOMPurify.sanitize(contentHtml, { USE_PROFILES: { html: true } }) : ''),
    [contentHtml]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col p-0 gap-0" data-testid="rich-content-modal">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border flex-shrink-0">
          <DialogTitle className="text-lg md:text-xl font-medium text-text_strong tracking-wide">
            {title}
          </DialogTitle>
        </DialogHeader>
        <div
          className="overflow-y-auto px-6 py-5 flex-1 prose prose-sm max-w-none"
          data-testid="rich-content-body"
        >
          {sanitized
            ? parse(sanitized)
            : <p className="text-sm text-text_muted">{t('common.noContent') || 'No content yet.'}</p>}
        </div>
        <DialogFooter className="px-6 py-4 border-t border-border flex-shrink-0 sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="rich-content-close">
            {t('common.close') || 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { pickLocalized };
